import { useMemo } from 'react';
import L from 'leaflet';
import { ModelPerformanceLegendItem } from 'types/Meteorology/Fields/ModelPerformance/mapData';

/**
 * Cria o HTML da legenda com faixas vindas da API (min, max, color, label).
 * selectedFeatureColor: quando informada, apenas o swatch com essa cor ganha destaque.
 * titleOverride: quando informado (ex.: período livre), substitui o título variável + unidade.
 */
function buildLegendOnAdd(
  legend: ModelPerformanceLegendItem[],
  variable: string,
  unit: string,
  selectedFeatureColor: string | null,
  titleOverride: string | null,
) {
  return () => {
    const div = L.DomUtil.create('div', 'heatmap-legend polygon-legend');
    const titleText = titleOverride ?? (variable ? `${variable}${unit ? ` (${unit})` : ''}` : '');
    const hasTitle = titleText.length > 0;
    let html = hasTitle
      ? `<div class="polygon-legend-title text-nowrap">${titleText}</div>`
      : '';

    legend.forEach((range) => {
      const isCategorical = range.max === null && range.min === 0;
      let text: string;
      if (isCategorical && range.label) {
        text = range.label;
      } else if (range.max === null) {
        text = `${range.min}+`;
      } else {
        text = `${range.min}&ndash;${range.max}`;
      }
      const labelSuffix = !isCategorical && range.label ? ` - ${range.label}` : '';
      const isHighlight = selectedFeatureColor != null && range.color === selectedFeatureColor;
      const swatchClass = `polygon-legend-swatch${isHighlight ? ' polygon-legend-swatch--highlight' : ''}`;
      html += `<div class="polygon-legend-row"><span class="${swatchClass}" style="background:${range.color}"></span><span class="polygon-legend-label">${text}${labelSuffix}</span></div>`;
    });

    div.innerHTML = html;
    return div;
  };
}

export default function usePolygonLegend(
  legend: ModelPerformanceLegendItem[] | undefined,
  variable: string,
  unit: string,
  selectedFeatureColor: string | null = null,
  titleOverride: string | null = null,
) {
  return useMemo(() => {
    if (!legend?.length) return undefined;

    const control = new L.Control({ position: 'bottomright' });
    control.onAdd = buildLegendOnAdd(legend, variable, unit, selectedFeatureColor, titleOverride);
    return control;
  }, [legend, variable, unit, selectedFeatureColor, titleOverride]);
}
